import { PayloadAction } from '@reduxjs/toolkit';
import {
  EContentType,
  IContentsComment,
  IRefundStore, IStore, IStoreBookmark, IStoreDetail, IStorePurchase, IStoreSalesTypes,
} from '@/store/types';
// import { GetStoreComment } from '@/fetches/user/store/comment/get-store-comment';
import { PostStoreProps, SalesTypeProps, StoreFiles } from '@/fetches/user/store/post-store';
import { createSliceSteamup } from '../common';

export interface paymentDetailProps {
  uuid: string,
  createdAt: Date,
  updatedAt: Date,
  storeCategoryID: number,
  storeContentType: EContentType,
  title: string,
  description: string;
  salesTypes: IStoreSalesTypes[]
}

const initialStoreDetail: IStoreDetail = {
  uuid: '',
  creatorUUID: '',
  creatorAvatarURL: '',
  creatorName: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  storeContentType: EContentType.MATH_2D,
  title: '',
  description: '',
  salesTypes: [],
  purchasedCnt: 0,
  keywords: [],
  storeCategoryID: 1,
  hasFollow: false,
};

export interface CustomSalesTypeProps {
  isFree: boolean | null; // 유/무료 여부 t: 무료 f: 유료
  price?: number; // 상품 금액
  discount?: number; // 할인 금액
  discountBeginAt?: number; // 할인 시작 기간
  discountEndAt?: number; // 할인 종료 기간
  isEditable: boolean | null; // 다운로드 설정 편집 가능 여부 t: 가능 f: 불가능
  memberContentUUID: string | null; // 학습 도구 업로드 원본 콘텐츠 ID
  files?: StoreFiles[]; // 기타 자료
}

export interface CustomPostStoreProps {
  storeCategoryID: number; // 카테고리
  title: string; // 상품 제목
  description?: string; // 상품 설명
  keywords?: number[]; // 상품 키워드
  isAgree: boolean; // 판매 동의 여부 t: 동의 f: 비동의(등록불가)
  salesTypes: CustomSalesTypeProps[]; // 판매 유형
}

const initialSalesType: CustomSalesTypeProps = {
  isFree: null,
  price: 0,
  discount: 0,
  discountBeginAt: 0,
  discountEndAt: 0,
  isEditable: null,
  memberContentUUID: null,
  files: [],
};

const initialPostStore: CustomPostStoreProps = {
  storeCategoryID: 1,
  title: '',
  description: '',
  keywords: [],
  isAgree: false,
  salesTypes: [initialSalesType, initialSalesType],
};

// nft types
export type NftListType = {
  tokenId: string;
  uri: string;
}

export type AllNFTs = {
  id: string;
  name: string;
  description: string;
  image_url: string;
}

export type ListType = 'all'|'mine';

interface IStoreState {
  loading: boolean;
  list: IStore[];
  storeDetail: IStoreDetail;
  purchaseList: IStorePurchase[];
  bookmarkList: IStoreBookmark[];
  recentViewList: IStore[];
  refundList: IRefundStore[];
  paymentDetail: paymentDetailProps;
  postStore: PostStoreProps;
  learningFilesContentType: string[];
  postStoreAddSecondType: boolean;
  filter: {
    keyword: number[];
    search: string;
  }
  paymentContent: {
    isAgree: boolean;
    point: number;
    mileage: number;
    sellTypeUUID: string;
  }
  currentPage: string;
  // check: 충돌 지점 - 확인하기
  // nft
  ethereum: any;
  walletAddress: string;
  initialized: boolean;

  listType: ListType
  nftList: NftListType[];
  allNFTs: AllNFTs[]; // 타입이 다름
  detail: AllNFTs;
  isDetail: boolean;

  lastNft: NftListType;
  onMinting: string; // 민팅 중일 땐 발급못하도록 막아야하기 때문
  storeComment: IContentsComment[];
  purchaseListCnt: number;
  totalBookmarkCnt: number;
  recentViewCnt: number;
  refuntListCnt: number;
}

const storesSlice = createSliceSteamup(
  'stores',
  {
    loading: false,
    list: [],
    storeDetail: initialStoreDetail,
    purchaseList: [],
    bookmarkList: [],
    recentViewList: [],
    refundList: [],
    paymentDetail: {
      uuid: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      storeCategoryID: 1,
      storeContentType: EContentType.MATH_2D,
      title: '',
      description: '',
      salesTypes: [],
    },
    storeCategory: {
      id: 0,
      title: '',
      storeCategoryOrder: 0,
      key: '',
    },
    postStore: initialPostStore,
    learningFilesContentType: ['', ''],
    postStoreAddSecondType: false,
    filter: {
      keyword: [],
      search: '',
    },
    paymentContent: {
      isAgree: false,
      point: 0,
      mileage: 0,
      sellTypeUUID: '',
    },
    currentPage: '',
    storeComment: [],

    // check: 충돌 지점 - 확인
    // nft
    ethereum: window as any,
    walletAddress: '',
    initialized: false,
    listType: 'all',
    nftList: [],
    allNFTs: [],
    detail: {
      id: '',
      contract: '',
      name: '',
      description: '',
      image_url: '',
      updated_at: '',
    },
    isDetail: false,
    lastNft: {
      tokenId: '',
      uri: '',
    },
    onMinting: '',
    purchaseListCnt: 0,
    totalBookmarkCnt: 0,
    recentViewCnt: 0,
    refuntListCnt: 0,
  } as IStoreState,
  {
    setListInit(state, { payload }: PayloadAction<(typeof state.list)>) {
      state.list = payload;
    },
    setStoreDetail(state, { payload }: PayloadAction<(typeof state.storeDetail)>) {
      state.storeDetail = payload;
    },
    setStoreDetailFollow(state, { payload }: PayloadAction<boolean>) {
      const updatedData = state.storeDetail;
      updatedData.hasFollow = payload;
      state.storeDetail = updatedData;
    },
    setQ(state, { payload }: PayloadAction<(typeof state.filter.search)>) { // setKeyword => setQ
      state.filter.search = payload;
    },
    setKeyword(state, { payload }: PayloadAction<(typeof state.filter.keyword)>) {
      state.filter.keyword = payload;
    },
    setBookmark(state, { payload }: PayloadAction<{uuid: string, type: number}>) {
      const { uuid, type } = payload;
      const findIndex = state.list.findIndex((item) => item.uuid === uuid);
      if (state.list[findIndex].salesTypes[type].isBookmark) {
        state.list[findIndex].salesTypes[type].isBookmark = false;
      } else {
        state.list[findIndex].salesTypes[type].isBookmark = true;
      }
    },
    setDetailBookmark(state, { payload }: PayloadAction<{bookmark: boolean, type: number}>) {
      const { bookmark, type } = payload;
      if (state.storeDetail.salesTypes[type].isBookmark) {
        state.storeDetail.salesTypes[type].isBookmark = bookmark;
      } else {
        state.storeDetail.salesTypes[type].isBookmark = bookmark;
      }
    },
    setPaymentDetail(state, { payload }: PayloadAction<(typeof state.paymentDetail)>) {
      state.paymentDetail = payload;
    },
    setStoreComment(state, { payload }: PayloadAction<(typeof state.storeComment)>) {
      state.storeComment = payload;
    },
    addStoreComment(state, { payload }: PayloadAction<IContentsComment[]>) {
      // const updatedData = [...state.storeComment];
      const updatedData = [...state.storeComment, ...payload];
      state.storeComment = updatedData;
    },
    deleteStoreComment(state, { payload }: PayloadAction<string>) {
      state.storeComment = state.storeComment.filter((item) => item.uuid !== payload);
    },
    reviseStoreComment(state, { payload }: PayloadAction<{ uuid: string, content: string }>) {
      const { uuid, content } = payload;
      state.storeComment = state.storeComment.map((item) => {
        if (item.uuid === uuid) {
          return { ...item, content };
        }
        return item;
      });
    },
    // ! 결제할 때 선택하는 내용
    setIsAgree(state, { payload }: PayloadAction<(typeof state.paymentContent.isAgree)>) {
      state.paymentContent.isAgree = payload;
    },
    setMileage(state, { payload }: PayloadAction<(typeof state.paymentContent.mileage)>) {
      state.paymentContent.mileage = payload;
    },
    setPoint(state, { payload }: PayloadAction<(typeof state.paymentContent.point)>) {
      state.paymentContent.point = payload;
    },
    setSalesTypeUUID(state, { payload }: PayloadAction<(typeof state.paymentContent.sellTypeUUID)>) {
      state.paymentContent.sellTypeUUID = payload;
    },
    // ! 판매할 때 선택하는 내용
    setPostStoreCategoryID(state, { payload }: PayloadAction<number>) {
      state.postStore.storeCategoryID = payload;
    },
    setPostStoreAddSecondType(state, { payload }: PayloadAction<boolean>) {
      state.postStoreAddSecondType = payload;
    },
    setPostStoreIsFree(state, { payload }: PayloadAction<{ index: number; free: boolean }>) {
      const { index, free } = payload;
      const updatedSalesTypes = [...state.postStore.salesTypes];
      updatedSalesTypes[index].isFree = free;
      state.postStore.salesTypes = updatedSalesTypes;
    },
    setPostStoreIsEditable(state, { payload }: PayloadAction<{ index: number; editable: boolean }>) {
      const { index, editable } = payload;
      const updatedSalesTypes = [...state.postStore.salesTypes];
      updatedSalesTypes[index].isEditable = editable;
      state.postStore.salesTypes = updatedSalesTypes;
    },
    /** 유형 1,2의 학습 도구 업로드의 유형이 다르면 안 돼서 확인하고자 추가함 */
    setPostStoreMemberContentFiles(state, { payload }: PayloadAction<{ index: number; type: string }>) {
      const { index, type } = payload;
      state.learningFilesContentType[index] = type;
    },
    setPostStoreMemberContentUUID(state, { payload }: PayloadAction<{ index: number; contentID: string }>) {
      const { index, contentID } = payload;
      const updatedSalesTypes = [...state.postStore.salesTypes];
      updatedSalesTypes[index].memberContentUUID = contentID;
      state.postStore.salesTypes = updatedSalesTypes;
    },
    deletePostStoreMemberContentUUID(state, { payload }:PayloadAction<{ index: number; contentID: string }>) {
      const { contentID, index } = payload;
      if (state.postStore.salesTypes[index].memberContentUUID === contentID) {
        state.postStore.salesTypes[index].memberContentUUID = '';
      }
    },
    deletePostStoreMemberContentFiles(state, { payload }:PayloadAction<{ index: number }>) {
      const { index } = payload;
      if (state.learningFilesContentType[index]) {
        state.learningFilesContentType[index] = '';
      }
    },
    deletePostStoreFiles(state, { payload }: PayloadAction<{index: number; fileURI: string}>) {
      const { index, fileURI } = payload;
      const salesType = state.postStore.salesTypes[index];
      if (salesType && salesType.files !== undefined) {
        salesType.files = salesType.files.filter((item) => item.fileURI !== fileURI);
      }
    },
    setPostStoreFiles(state, { payload }: PayloadAction<{ index: number; files: StoreFiles[] }>) {
      const { index, files } = payload;
      state.postStore.salesTypes[index].files = files;
    },
    setPostStoreTitle(state, { payload }: PayloadAction<(typeof state.postStore.title)>) {
      state.postStore.title = payload;
    },
    setPostStoreDescription(state, { payload }: PayloadAction<(typeof state.postStore.description)>) {
      state.postStore.description = payload;
    },
    setPostStoreKeyword(state, { payload }: PayloadAction<number[]>) {
      state.postStore.keywords = payload;
    },
    setPostStoreIsAgree(state, { payload }: PayloadAction<boolean>) {
      state.postStore.isAgree = payload;
    },
    setPostStorePrice(state, { payload }: PayloadAction<{index: number, price: number}>) {
      const { index, price } = payload;
      const updatedSalesTypes = [...state.postStore.salesTypes];
      updatedSalesTypes[index].price = price;
      state.postStore.salesTypes = updatedSalesTypes;
    },
    setPostStoreDiscount(state, { payload }: PayloadAction<{index: number, discount: number}>) {
      const { index, discount } = payload;
      const updatedSalesTypes = [...state.postStore.salesTypes];
      updatedSalesTypes[index].discount = discount;
      state.postStore.salesTypes = updatedSalesTypes;
    },
    setPostStoreDiscountBeginAt(state, { payload }: PayloadAction<{index: number, beginAt: number}>) {
      const { index, beginAt } = payload;
      state.postStore.salesTypes[index].discountBeginAt = Math.floor(beginAt / 1000);
    },
    setPostStoreDiscountEndAt(state, { payload }: PayloadAction<{index: number, endAt: number}>) {
      const { index, endAt } = payload;
      state.postStore.salesTypes[index].discountEndAt = Math.floor(endAt / 1000);
    },
    // ! 스토어 관리 페이지
    setPurchaseList(state, { payload }: PayloadAction<(typeof state.purchaseList)>) {
      state.purchaseList = payload;
    },
    setPurchaseConfirmation(state, { payload }: PayloadAction<{date: Date, uuid: string}>) {
      const { date, uuid } = payload;
      const updated = state.purchaseList;
      const findData = updated.find((item) => item.uuid === uuid);
      if (findData) {
        findData.salesTypes.purchaseConfirmedAt = date;
      }
      state.purchaseList = updated;
    },
    setBookmarkList(state, { payload }: PayloadAction<(typeof state.bookmarkList)>) {
      state.bookmarkList = payload;
    },
    undoBookmark(state, { payload }: PayloadAction<{ uuid: string }>) {
      const { uuid } = payload;
      const updatedData = state.bookmarkList;
      const checkIndex = updatedData.findIndex((item) => item.uuid === uuid);
      if (checkIndex !== -1) {
        updatedData.splice(checkIndex, 1);
      }
      state.bookmarkList = updatedData;
    },
    setRecentViewList(state, { payload }: PayloadAction<(typeof state.recentViewList)>) {
      state.recentViewList = payload;
    },
    recentViewListBookmark(state, { payload }: PayloadAction<{uuid: string, type: number}>) {
      const { uuid, type } = payload;
      const updatedList = state.recentViewList;
      const findIndex = updatedList.findIndex((item) => item.uuid === uuid);
      if (updatedList[findIndex].salesTypes[type].isBookmark) {
        updatedList[findIndex].salesTypes[type].isBookmark = false;
      } else {
        updatedList[findIndex].salesTypes[type].isBookmark = true;
      }
      state.recentViewList = updatedList;
    },
    setRefundList(state, { payload }: PayloadAction<(typeof state.refundList)>) {
      state.refundList = payload;
    },
    // setRefundStoreDetail(state, { payload }: PayloadAction<(typeof state.refundStoreDetail)>) {
    //   state.refundStoreDetail = payload;
    // },

    // nft 추가
    setWalletAddress(state, { payload }: PayloadAction<(typeof state.walletAddress)>) {
      state.walletAddress = payload;
    },
    pushNFTList(state, { payload }: PayloadAction<NftListType>) {
      state.nftList = [...state.nftList, payload];
    },

  },
);

export default storesSlice;
